import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { generatePlayerForMarket } from '../utils/gameData';
import { GraduationCap, UserPlus, ArrowClockwise } from '@phosphor-icons/react';

const generateYouthProspects = (count = 3) => {
  const prospects = [];
  const positions = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'ST'];
  for (let i = 0; i < count; i++) {
    const pos = positions[Math.floor(Math.random() * positions.length)];
    const prospect = generatePlayerForMarket(pos);
    prospect.age = Math.floor(Math.random() * 3) + 16; // 16-18
    prospect.overall = Math.floor(Math.random() * 11) + 50; // 50-60 (raw talent)
    prospect.potential = Math.floor(Math.random() * 15) + 85; // 85-99 (high ceiling)
    prospect.isYouthAcademy = true;
    prospects.push(prospect);
  }
  return prospects;
};

export const YouthAcademy = ({ team, onPromotePlayer, season }) => {
  const [prospects, setProspects] = useState(() => {
    const saved = team?.youthAcademy;
    return saved && saved.length > 0 ? saved : generateYouthProspects(3);
  });

  const getPositionColor = (position) => {
    if (position === 'GK') return 'position-gk';
    if (['CB', 'LB', 'RB', 'LWB', 'RWB'].includes(position)) return 'position-def';
    if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(position)) return 'position-mid';
    return 'position-att';
  };

  const handlePromote = (player) => {
    onPromotePlayer(player);
    setProspects(prev => prev.filter(p => p.id !== player.id));
  };

  const refreshAcademy = () => {
    setProspects(generateYouthProspects(3));
  };

  return (
    <div className="card-surface rounded-sm" data-testid="youth-academy">
      <div className="border-b border-[#2C2C35] px-4 py-3 flex items-center justify-between">
        <h3 className="font-heading font-bold text-white uppercase tracking-wide flex items-center gap-2">
          <GraduationCap size={18} weight="fill" className="text-[#007AFF]" />
          Youth Academy
        </h3>
        <Button size="sm" variant="ghost" onClick={refreshAcademy} className="text-[#6E6E73] hover:text-white" data-testid="refresh-academy-btn">
          <ArrowClockwise size={14} />
        </Button>
      </div>
      <div className="p-2">
        <p className="text-xs text-[#6E6E73] px-2 mb-2">Season {season} youth prospects. High potential talents from the academy system.</p>
        <ScrollArea className="h-[200px]">
          <div className="space-y-1">
            {prospects.map(player => (
              <div key={player.id} className="flex items-center gap-2 px-2 py-2 hover:bg-[#1A1A1F] rounded-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="nationality-flag text-sm">{player.flag}</span>
                    <p className="text-sm font-medium text-white truncate">{player.name}</p>
                    <span className={`position-badge ${getPositionColor(player.position)}`}>{player.position}</span>
                  </div>
                  <p className="text-[10px] text-[#6E6E73] mt-0.5">
                    Age {player.age} | OVR {player.overall} | POT <span className="text-[#007AFF]">{player.potential}</span>
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handlePromote(player)}
                  className="bg-[#007AFF] hover:bg-[#0A84FF] text-white text-xs flex-shrink-0"
                  data-testid={`promote-${player.id}`}
                >
                  <UserPlus size={12} className="mr-1" /> Promote
                </Button>
              </div>
            ))}
            {prospects.length === 0 && (
              <p className="text-center text-[#6E6E73] py-4 text-sm">All prospects promoted or no new talent this season.</p>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
